import { memo } from 'react';
import { css } from '@emotion/react';
import { theme } from '@frontend/theme';
import { Button } from '@frontend/design-system';
import { useZoomStore } from '../store/zoom.store';

interface ZoomControlsProps {
  className?: string;
}

const isMac = typeof navigator !== 'undefined' && /Mac/.test(navigator.platform);
const modifierLabel = isMac ? '⌘' : 'Ctrl';

const ZoomControlsComponent = ({ className }: ZoomControlsProps) => {
  const { showControls, zoomIn, zoomOut, canZoomIn, canZoomOut } = useZoomStore([
    'showControls',
    'zoomIn',
    'zoomOut',
    'canZoomIn',
    'canZoomOut',
  ]);

  if (!showControls) {
    return null;
  }

  return (
    <div className={className} css={containerStyle}>
      <Button
        variant='secondary'
        iconName='collapse-small'
        hoverLabel={`Collapse view (${modifierLabel}−)`}
        onClick={zoomIn}
        disabled={!canZoomIn}
      />
      <Button
        variant='secondary'
        iconName='expand-small'
        hoverLabel={`Expand view (${modifierLabel}+)`}
        onClick={zoomOut}
        disabled={!canZoomOut}
      />
    </div>
  );
};

export const ZoomControls = memo(ZoomControlsComponent);

const containerStyle = css({
  position: 'sticky',
  bottom: theme.spacing(4),
  left: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  gap: theme.spacing(1),
  paddingRight: theme.spacing(2),
  zIndex: 1,
  gridRow: '-1',
  gridColumn: '1 / -1',
  // Stick horizontally and constrain width to visible area
  width: 'var(--viewport-width)',
  boxSizing: 'border-box',
  // Collapse height so it doesn't add extra space to the grid
  height: 0,
  overflow: 'visible',
  alignSelf: 'end',
  'button, button[aria-disabled="true"]': {
    background: theme.colors.white,
    border: `1px solid ${theme.colors.neutral20}`,
  },
});
